import React, { useState, useEffect } from 'react';
import { Phone, Mail, Clock, MapPin, MessageCircle, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mock';
import { companyApi, analyticsApi } from '../hooks/useApi';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(mockData.contact);

  useEffect(() => {
    // Load real company info from backend
    const loadCompanyInfo = async () => {
      try {
        const info = await companyApi.getInfo();
        setCompanyInfo(info);
      } catch (error) {
        console.error('Error loading company info:', error);
        // Fallback to mock data
      }
    };
    loadCompanyInfo();
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleWhatsApp = async () => {
    try {
      await analyticsApi.trackClick('whatsapp');
    } catch (error) {
      console.error('Error tracking WhatsApp click:', error);
    }
    // Using direct WhatsApp URL format for better compatibility
    const phoneNumber = companyInfo.whatsapp;
    const message = encodeURIComponent('Hola, me gustaría solicitar información sobre sus servicios');
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    
    // Try to open in a new window with specific features to avoid iframe blocking
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      // Fallback: create a temporary link and click it
      const link = document.createElement('a');
      link.href = whatsappUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePhoneCall = async () => {
    try {
      await analyticsApi.trackClick('phone');
    } catch (error) {
      console.error('Error tracking phone click:', error);
    }
    window.open(`tel:${companyInfo.phone.replace(/\s/g, '')}`, '_self');
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#004534] border-b border-green-700 py-2 px-4 hidden md:block">
        <div className="container mx-auto flex justify-end items-center text-sm">
          <div className="flex items-center space-x-6 text-white">
            <button 
              onClick={handlePhoneCall}
              className="flex items-center space-x-1 hover:text-orange-300 transition-colors"
            >
              <Phone className="h-4 w-4 text-[#f97316]" />
              <span>{companyInfo.phone}</span>
            </button>
            <div className="flex items-center space-x-1">
              <Mail className="h-4 w-4 text-[#f97316]" />
              <span>{companyInfo.email}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4 text-[#f97316]" />
              <span>{companyInfo.hours}</span>
            </div>
            <button 
              onClick={() => {
                const address = "Calle Ntra. Sra. de las Nieves, Edificio Piscis Local 4, 29770 Torrox, Málaga";
                const encodedAddress = encodeURIComponent(address);
                window.open(`https://maps.google.com/?q=${encodedAddress}`, '_blank');
              }}
              className="flex items-center space-x-1 text-[#f97316] hover:text-[#ea580c] transition-colors"
            >
              <MapPin className="h-4 w-4" />
              <span>Dónde estamos</span>
            </button>
            <button 
              onClick={handleWhatsApp}
              className="flex items-center space-x-1 text-[#f97316] hover:text-[#ea580c] transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              <span>WhatsApp</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="bg-white sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-none md:rounded-3xl md:my-2 px-4 md:px-8 py-3 md:py-4 flex items-center justify-between border border-gray-200">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_farmtools-repair/artifacts/fs2r1blo_gif.3.gif" 
                alt="ERBA Service - Logo animado especialistas en reparación de maquinaria agrícola en Torrox y Axarquía" 
                className="h-28 md:h-32 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#inicio" className="text-[#004534] hover:text-[#16a34a] transition-colors font-medium">
                Inicio
              </a>
              <a href="#servicios" className="text-[#004534] hover:text-[#16a34a] transition-colors font-medium">
                Servicios
              </a>
              <a href="#nosotros" className="text-[#004534] hover:text-[#16a34a] transition-colors font-medium">
                Nosotros
              </a>
              <a href="#contacto" className="text-[#004534] hover:text-[#16a34a] transition-colors font-medium">
                Contacto
              </a>
              <Button 
                onClick={handleWhatsApp}
                className="bg-[#16a34a] hover:bg-[#15803d] text-white px-6 py-2 rounded-full transition-all duration-200 transform hover:scale-105 font-semibold"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={toggleMenu}
              className="md:hidden text-[#004534] p-2"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-white border border-gray-200 rounded-b-2xl px-4 pb-4 space-y-4 shadow-lg">
              <a href="#inicio" className="block w-full text-left text-[#004534] hover:text-[#16a34a] transition-colors py-2" onClick={() => setIsMenuOpen(false)}>
                Inicio
              </a>
              <a href="#servicios" className="block w-full text-left text-[#004534] hover:text-[#16a34a] transition-colors py-2" onClick={() => setIsMenuOpen(false)}>
                Servicios
              </a>
              <a href="#nosotros" className="block w-full text-left text-[#004534] hover:text-[#16a34a] transition-colors py-2" onClick={() => setIsMenuOpen(false)}>
                Nosotros
              </a>
              <a href="#contacto" className="block w-full text-left text-[#004534] hover:text-[#16a34a] transition-colors py-2" onClick={() => setIsMenuOpen(false)}>
                Contacto
              </a>
              <div className="pt-4 space-y-2">
                <Button 
                  onClick={handleWhatsApp}
                  className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white rounded-full"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
                <div className="flex justify-center space-x-4 pt-2">
                  <button 
                    onClick={handlePhoneCall}
                    className="text-[#16a34a] hover:text-[#15803d] transition-colors"
                    title="Llamar"
                  >
                    <Phone className="h-5 w-5" />
                  </button>
                  <button onClick={handleWhatsApp} className="text-[#16a34a] hover:text-[#15803d] transition-colors">
                    <MessageCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};